import * as React from 'react';
import { degreesToRadians, roundToOne } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import { IVerticalDropCrossProps } from './IVerticalDrop';
import { getImgSrcWithColor, loadImage } from '../../../../../utils/graphicEngine';
import groundImage from '../../../../../images/ground_pattern.svg';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { setCrossSectionImg } from '../../../../store/project/resultsSlice';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//setup
const setup = {
    gabionColor: 'grey',
    borderGabionColor: 'black',
    waterColor: '#83d7ee',

    indicatorHeight: 30,
    indicatorWidth: 40,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    borderWidth: 1.5,
    height: 370,
    width: 540,
    heightSoil: 2,
    borderWings: 1,
};

const VerticalDropCross = (props: IVerticalDropCrossProps) => {
    const verticalDropCanvas = React.useRef<HTMLCanvasElement>(null);
    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    const riverGeometry = { ...props.riverGeometry };
    const weirGeometry = { ...props.weirGeometry };
    const wingsGeometry = { ...props.wingsGeometry };
    const dispatch = useAppDispatch();

    const validationInput = () => {
        if (wingsGeometry.slopeAngle! < 0) {
            wingsGeometry.slopeAngle = 0;
        }
        if (wingsGeometry.slopeAngle! > 90) {
            wingsGeometry.slopeAngle = 90;
        }
    };

    validationInput();

    let angleLeftRad = degreesToRadians(riverGeometry.slopeOfLeftBank!);
    let hypLeft = riverGeometry.bankTopElevation / Math.sin(angleLeftRad);
    let cxLeft = hypLeft * Math.cos(angleLeftRad);

    let angleRightRad = degreesToRadians(riverGeometry.slopeOfRightBank!);
    let hypRight = riverGeometry.bankTopElevation / Math.sin(angleRightRad);
    let cxRight = hypRight * Math.cos(angleRightRad);

    const _height = riverGeometry.bankTopElevation + weirGeometry.foundation + setup.heightSoil;
    const _width = setup.borderWidth + cxLeft + riverGeometry.channelWidth + cxRight + setup.borderWidth;

    const scaleWidth = (setup.width + props.scale) / _width;
    const scaleHeight = (setup.height + props.scale) / _height;

    const _scale = Math.ceil(Math.min(scaleWidth, scaleHeight));

    React.useEffect(() => {
        let slopedWeirCanvasEleL = verticalDropCanvas.current!;
        ctx = slopedWeirCanvasEleL.getContext('2d')!;
        Promise.all([loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!props.result) {
                createWaterSection();
            }
            createRiverGeometry();
            createIndicator();
            dispatch(setCrossSectionImg(slopedWeirCanvasEleL.toDataURL('image/png')));
        });
    }, [props]);

    const createWaterSection = () => {
        let z0 = props.result!.hydraulicResult.backwaterDepth;
        let layerHeight = 0;
        weirGeometry.layer.forEach(x => {
            if (x.layerNumber !== 1) {
                layerHeight += x.layerHeight;
            }
        });
        ctx.beginPath();
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation + weirGeometry.foundation - layerHeight - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation + weirGeometry.foundation - layerHeight - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation + weirGeometry.foundation - layerHeight));
        ctx.lineTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation + weirGeometry.foundation - layerHeight));
        ctx.fill();
        ctx.closePath();
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const createIndicator = () => {
        ctx.save();
        //river geometry bank top elevation
        createIndicatorY(0, _scale * riverGeometry.bankTopElevation, setup.indicatorWidth / 2, riverGeometry.bankTopElevation);
        //weir geometry height
        let layerHeight = 0;
        weirGeometry.layer.forEach(x => {
            if (x.layerNumber !== 1) {
                layerHeight += x.layerHeight;
            }
        });
        createIndicatorY(
            _scale * (riverGeometry.bankTopElevation - layerHeight + weirGeometry.foundation),
            _scale * riverGeometry.bankTopElevation,
            setup.indicatorWidth + _scale * _width + setup.indicatorWidth / 2,
            layerHeight - weirGeometry.foundation,
        );
        //river geometry channel width
        createIndicatorX(
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft),
            setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth),
            _scale * _height + setup.indicatorHeight / 2,
            riverGeometry.channelWidth,
        );
        ctx.restore();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], x - 2, moveToY, x + 2, moveToY);
        createLine([], x, moveToY, x, lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], x - 2, lineToY, x + 2, lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], moveToX, y - 2, moveToX, y + 2);
        createLine([], moveToX, y, lineToX, y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], lineToX, y - 2, lineToX, y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const cleanCanvas = () => {
        let canvasEle = verticalDropCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const createRectWithBorder = (x: number, y: number, w: number, h: number, pat: string | CanvasPattern | CanvasGradient) => {
        ctx.fillStyle = pat;
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.strokeRect(x, y, w, h);
    };

    const createRiverGeometry = () => {
        //river geometry
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft), _scale * riverGeometry.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth), _scale * riverGeometry.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth + cxRight), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, 0);
        ctx.fill();
        ctx.closePath();
        ctx.beginPath();
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft), _scale * riverGeometry.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth), _scale * riverGeometry.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth + cxRight), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, 0);
        ctx.stroke();
        ctx.closePath();

        let layerHeight = 0;
        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');
        weirGeometry.layer
            .slice()
            .reverse()
            .forEach((x, i) => {
                layerHeight += x.layerHeight;
                let offsetLeft = (layerHeight - weirGeometry.foundation) * Math.tan(Math.PI / 2 - angleLeftRad);
                let offsetRight = (layerHeight - weirGeometry.foundation) * Math.tan(Math.PI / 2 - angleRightRad);
                if (x.layerNumber == 1) {
                    //wings
                    ctx.beginPath();
                    ctx.fillStyle = basePat!;
                    ctx.moveTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft - offsetLeft - setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth +
                            _scale *
                                (setup.borderWidth +
                                    cxLeft +
                                    (riverGeometry.channelWidth - wingsGeometry.width!) / 2 +
                                    wingsGeometry.offset! -
                                    wingsGeometry.height! / Math.tan(degreesToRadians(wingsGeometry.slopeAngle!))),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + (riverGeometry.channelWidth - wingsGeometry.width!) / 2 + wingsGeometry.offset!),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight + wingsGeometry.height!),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft - offsetLeft - setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight + wingsGeometry.height!),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft - offsetLeft - setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.fill();
                    ctx.stroke();
                    ctx.closePath();

                    ctx.beginPath();
                    ctx.fillStyle = basePat!;
                    ctx.moveTo(
                        setup.indicatorWidth +
                            _scale *
                                (setup.borderWidth +
                                    cxLeft +
                                    riverGeometry.channelWidth -
                                    (riverGeometry.channelWidth - wingsGeometry.width!) / 2 +
                                    wingsGeometry.offset! +
                                    wingsGeometry.height! / Math.tan(degreesToRadians(wingsGeometry.slopeAngle!))),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth + offsetRight + setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth + offsetRight + setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight + wingsGeometry.height!),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth + _scale * (setup.borderWidth + cxLeft + riverGeometry.channelWidth - (riverGeometry.channelWidth - wingsGeometry.width!) / 2 + wingsGeometry.offset!),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight + wingsGeometry.height!),
                    );
                    ctx.lineTo(
                        setup.indicatorWidth +
                            _scale *
                                (setup.borderWidth +
                                    cxLeft +
                                    riverGeometry.channelWidth -
                                    (riverGeometry.channelWidth - wingsGeometry.width!) / 2 +
                                    wingsGeometry.offset! +
                                    wingsGeometry.height! / Math.tan(degreesToRadians(wingsGeometry.slopeAngle!))),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                    );
                    ctx.fill();
                    ctx.stroke();
                    ctx.closePath();
                } else {
                    createRectWithBorder(
                        setup.indicatorWidth + _scale * (+setup.borderWidth + cxLeft - offsetLeft - setup.borderWidth),
                        _scale * (riverGeometry.bankTopElevation + weirGeometry.foundation - layerHeight),
                        _scale * (setup.borderWidth + offsetLeft + riverGeometry.channelWidth + offsetRight + setup.borderWidth),
                        _scale * x.layerHeight,
                        basePat!,
                    );
                }
            });
    };

    return <canvas width={_scale * _width + 2 * setup.indicatorWidth} height={_scale * _height + setup.indicatorHeight} ref={verticalDropCanvas}></canvas>;
};

export default VerticalDropCross;
